/**
 * Debug logging utility
 * Provides centralized, configurable logging with categories and levels
 *
 * Usage:
 *   import Debug from './utils/Debug.js';
 *   Debug.log('MyCategory', 'message', data);
 *   Debug.warn('MyCategory', 'warning message');
 *   Debug.error('MyCategory', 'error message');
 *
 * Configuration:
 *   Debug.enabled = true;           // Enable/disable all logging
 *   Debug.setLevel('warn');         // Set minimum level: 'log', 'warn', 'error', 'none'
 *   Debug.enableCategory('World');  // Enable specific category
 *   Debug.disableCategory('World'); // Disable specific category
 *   Debug.enableAllCategories();    // Enable all categories
 *   Debug.disableAllCategories();   // Disable all (then enable specific ones)
 */

const LEVELS = {
  log: 0,
  warn: 1,
  error: 2,
  none: 3
};

class DebugLogger {
  constructor() {
    // Master switch - set to false for production
    this.enabled = false;

    // Minimum log level to display
    this.level = LEVELS.log;

    // Category filters - if empty, all categories are allowed
    this.enabledCategories = new Set();
    this.disabledCategories = new Set();
    this.categoryMode = 'all'; // 'all', 'whitelist', 'blacklist'

    // Performance tracking
    this.timers = new Map();
  }

  /**
   * Set the minimum log level
   * @param {'log'|'warn'|'error'|'none'} level
   */
  setLevel(level) {
    if (LEVELS[level] !== undefined) {
      this.level = LEVELS[level];
    }
  }

  /**
   * Enable a specific category
   * @param {string} category
   */
  enableCategory(category) {
    this.disabledCategories.delete(category);
    this.enabledCategories.add(category);
    if (this.categoryMode === 'all') {
      this.categoryMode = 'whitelist';
    }
  }

  /**
   * Disable a specific category
   * @param {string} category
   */
  disableCategory(category) {
    this.enabledCategories.delete(category);
    this.disabledCategories.add(category);
    if (this.categoryMode === 'whitelist') {
      this.categoryMode = 'blacklist';
    }
  }

  /**
   * Enable all categories
   */
  enableAllCategories() {
    this.enabledCategories.clear();
    this.disabledCategories.clear();
    this.categoryMode = 'all';
  }

  /**
   * Disable all categories (use enableCategory to whitelist specific ones)
   */
  disableAllCategories() {
    this.enabledCategories.clear();
    this.disabledCategories.clear();
    this.categoryMode = 'whitelist';
  }

  /**
   * Check if a category should be logged
   * @param {string} category
   * @returns {boolean}
   */
  isCategoryEnabled(category) {
    switch (this.categoryMode) {
      case 'all':
        return true;
      case 'whitelist':
        return this.enabledCategories.has(category);
      case 'blacklist':
        return !this.disabledCategories.has(category);
      default:
        return true;
    }
  }

  /**
   * Internal method to format and output log messages
   */
  _output(level, category, args) {
    if (!this.enabled) return;
    if (LEVELS[level] < this.level) return;
    if (!this.isCategoryEnabled(category)) return;

    const prefix = `[${category}]`;
    const method = level === 'log' ? 'log' : level;

    console[method](prefix, ...args);
  }

  /**
   * Log a message (lowest priority)
   * @param {string} category - Category name for filtering
   * @param {...any} args - Arguments to log
   */
  log(category, ...args) {
    this._output('log', category, args);
  }

  /**
   * Log a warning (medium priority)
   * @param {string} category - Category name for filtering
   * @param {...any} args - Arguments to log
   */
  warn(category, ...args) {
    this._output('warn', category, args);
  }

  /**
   * Log an error (high priority, always shown unless level is 'none')
   * @param {string} category - Category name for filtering
   * @param {...any} args - Arguments to log
   */
  error(category, ...args) {
    this._output('error', category, args);
  }

  /**
   * Start a performance timer
   * @param {string} label - Timer label
   */
  time(label) {
    if (!this.enabled) return;
    this.timers.set(label, performance.now());
  }

  /**
   * End a performance timer and log the duration
   * @param {string} category - Category for the log
   * @param {string} label - Timer label
   */
  timeEnd(category, label) {
    if (!this.enabled) return;
    const start = this.timers.get(label);
    if (start !== undefined) {
      const duration = performance.now() - start;
      this.log(category, `${label}: ${duration.toFixed(2)}ms`);
      this.timers.delete(label);
    }
  }

  /**
   * Log only once per unique key (useful for warnings that shouldn't spam)
   * @param {string} key - Unique key to track
   * @param {string} category - Category name
   * @param {...any} args - Arguments to log
   */
  once(key, category, ...args) {
    if (!this._onceKeys) this._onceKeys = new Set();
    if (this._onceKeys.has(key)) return;
    this._onceKeys.add(key);
    this.log(category, ...args);
  }

  /**
   * Log with a group (collapsible in console)
   * @param {string} category - Category name
   * @param {string} label - Group label
   * @param {Function} fn - Function containing logs to group
   */
  group(category, label, fn) {
    if (!this.enabled || !this.isCategoryEnabled(category)) return;
    console.group(`[${category}] ${label}`);
    fn();
    console.groupEnd();
  }

  /**
   * Log with a collapsed group
   * @param {string} category - Category name
   * @param {string} label - Group label
   * @param {Function} fn - Function containing logs to group
   */
  groupCollapsed(category, label, fn) {
    if (!this.enabled || !this.isCategoryEnabled(category)) return;
    console.groupCollapsed(`[${category}] ${label}`);
    fn();
    console.groupEnd();
  }

  /**
   * Create a scoped logger for a specific category
   * @param {string} category - Category name
   * @returns {Object} Scoped logger with log, warn, error methods
   */
  scope(category) {
    return {
      log: (...args) => this.log(category, ...args),
      warn: (...args) => this.warn(category, ...args),
      error: (...args) => this.error(category, ...args),
      time: (label) => this.time(label),
      timeEnd: (label) => this.timeEnd(category, label),
      once: (key, ...args) => this.once(key, category, ...args),
      group: (label, fn) => this.group(category, label, fn),
      groupCollapsed: (label, fn) => this.groupCollapsed(category, label, fn)
    };
  }
}

// Singleton instance
const Debug = new DebugLogger();

// Expose for easy configuration in browser console
if (typeof window !== 'undefined') {
  window.Debug = Debug;
}

export default Debug;
